import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  Avatar,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { chartTooltipProps } from '@/lib/chartTheme';
import type { CustomerSatisfactionData, SatisfactionTrend } from '@/types';
import { dateFnsLocale } from '@/utils/locale';
import {
  formatChartValue,
  formatMeasured,
  hasPlottablePoint,
  hasSatisfactionData,
  isMeasured,
} from '../reportMeasurements';
import { CompactNoData, ReportSectionShell } from './ReportSectionShell';

interface CustomerSatisfactionSectionProps {
  satisfactionData: CustomerSatisfactionData;
  satisfactionTrend: SatisfactionTrend[];
  isMobile: boolean;
}

export function CustomerSatisfactionSection({
  satisfactionData,
  satisfactionTrend,
  isMobile,
}: CustomerSatisfactionSectionProps) {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  // A period without ratings is one compact line, not zero cards and empty charts.
  if (!hasSatisfactionData(satisfactionData, satisfactionTrend)) {
    return <CompactNoData title={t('customerSatisfaction')} testId="customer-satisfaction-section" />;
  }

  const { overallScore, nps, responseRate, avgResponseTime, satisfactionChange } = satisfactionData;
  const showTrend = hasPlottablePoint(satisfactionTrend, ['score']);

  const ratingDistribution = [
    { rating: '5 ★', count: satisfactionData.distribution.excellent, color: theme.palette.success.main },
    { rating: '4 ★', count: satisfactionData.distribution.good, color: theme.palette.success.light },
    { rating: '3 ★', count: satisfactionData.distribution.average, color: theme.palette.warning.light },
    { rating: '2 ★', count: satisfactionData.distribution.poor, color: theme.palette.warning.main },
    { rating: '1 ★', count: satisfactionData.distribution.terrible, color: theme.palette.error.main },
  ];

  return (
    <ReportSectionShell title={t('customerSatisfaction')} testId="customer-satisfaction-section">
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2, border: 1, borderColor: 'success.200', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SentimentSatisfiedAltIcon sx={{ color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('overallScore')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="h4" fontWeight={700} color="success.main" data-testid="satisfaction-overall-score">
                  {formatMeasured(overallScore, 1)}
                </Typography>
                {isMeasured(overallScore) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        sx={{
                          fontSize: 16,
                          color: i < Math.floor(overallScore) ? 'warning.main' : 'grey.300',
                        }}
                      />
                    ))}
                  </Box>
                ) : null}
              </Box>
              {!isMeasured(overallScore) && (
                <Typography variant="caption" color="text.secondary">
                  {t('notMeasured')}
                </Typography>
              )}
              {isMeasured(satisfactionChange) && satisfactionChange > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption" color="success.main">
                    +{satisfactionChange.toFixed(1)}% {t('vsPrevious')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, border: 1, borderColor: 'primary.200', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ThumbUpIcon sx={{ color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('npsScore')}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="primary.main" data-testid="satisfaction-nps">
                {formatMeasured(nps, 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {!isMeasured(nps)
                  ? t('npsNotMeasured')
                  : nps >= 70
                    ? t('npsExcellent')
                    : nps >= 50
                      ? t('npsGood')
                      : nps >= 30
                        ? t('npsSatisfactory')
                        : t('npsWeak')}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2, border: 1, borderColor: 'info.200', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon sx={{ color: 'info.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('responses')}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {satisfactionData.totalResponses}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isMeasured(responseRate)
                  ? `${responseRate.toFixed(1)}% ${t('responseRate')}`
                  : `${t('responseRate')}: ${t('notMeasured')}`}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, border: 1, borderColor: 'warning.200', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon sx={{ color: 'warning.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('avgResponseTime')}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="warning.main" data-testid="satisfaction-response-time">
                {formatMeasured(avgResponseTime, 1, 'h')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isMeasured(avgResponseTime) ? t('avgResponseTimeDesc') : t('notMeasured')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('ratingDistribution')}
            </Typography>
            <Box sx={{ p: 2 }}>
              <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                <BarChart
                  data={ratingDistribution}
                  layout="vertical"
                  margin={isMobile ? { top: 5, right: 10, left: 0, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="rating" type="category" width={50} />
                  <Tooltip {...chartTooltipProps(theme)} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {ratingDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          {showTrend && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('satisfactionTrend')}
            </Typography>
            <Box sx={{ p: 2 }}>
              <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                <LineChart
                  data={satisfactionTrend}
                  margin={isMobile ? { top: 5, right: 10, left: 0, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => format(new Date(value), 'd.M.', { locale: dateFnsLocale() })}
                  />
                  <YAxis domain={[0, 5]} />
                  <Tooltip
                    {...chartTooltipProps(theme)}
                    labelFormatter={(value) => format(new Date(value as string), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    formatter={(value) => formatChartValue(value, t('notMeasured'))}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke={theme.palette.success.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                    name={t('average')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          )}

          {satisfactionData.topComplaints.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThumbDownIcon sx={{ fontSize: 18, color: 'error.main' }} />
              {t('topComplaints')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  {satisfactionData.topComplaints.map((complaint, index) => (
                    <TableRow key={complaint.issue} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'error.light' }}>
                            {index + 1}
                          </Avatar>
                          <Typography variant="body2">{complaint.issue}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={complaint.count} size="small" color="error" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          )}

          {satisfactionData.topPraises.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThumbUpIcon sx={{ fontSize: 18, color: 'success.main' }} />
              {t('topPraises')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  {satisfactionData.topPraises.map((praise, index) => (
                    <TableRow key={praise.aspect} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'success.light' }}>
                            {index + 1}
                          </Avatar>
                          <Typography variant="body2">{praise.aspect}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={praise.count} size="small" color="success" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          )}
        </Grid>
    </ReportSectionShell>
  );
}
